import type {
  CaseRecordAuditEntry,
  GetCaseRecordHistoryRequest,
} from '@compass/client/types/case_record';
import { AuditActorType } from '@compass/client/types/case_record';
import type { User } from '@compass/client/types/user';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { caseRecordService, userService } from '@/services/api';
import { useStore } from '@/store';

interface UseCaseHistoryOptions {
  caseRecordId: string;
  enabled?: boolean;
  /** Filter by actor type. Defaults to AUDIT_ACTOR_TYPE_USER. */
  actorType?: AuditActorType;
}

const PAGE_SIZE = 100;

/**
 * Hook to fetch case record history with infinite scroll pagination.
 * Uses cursor-based pagination with next_cursor from the API response.
 */
export function useCaseHistory(options: UseCaseHistoryOptions) {
  const { selectedOrganizationId, selectedProgramId } = useStore();
  const actorType = options.actorType ?? AuditActorType.AUDIT_ACTOR_TYPE_USER;

  const query = useInfiniteQuery({
    queryKey: ['caseHistory', options.caseRecordId, actorType],
    queryFn: async ({ pageParam }) => {
      if (!selectedOrganizationId || !selectedProgramId) {
        throw new Error('Organization and program context required');
      }

      const request: GetCaseRecordHistoryRequest = {
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: options.caseRecordId,
        limit: PAGE_SIZE,
        start_after_id: pageParam,
        actor_type: actorType,
      };

      return caseRecordService.getCaseRecordHistory(request);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.next_cursor || undefined,
    enabled: options.enabled && !!selectedOrganizationId && !!selectedProgramId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Flatten all entries from all pages
  const entries = useMemo((): CaseRecordAuditEntry[] => {
    return query.data?.pages.flatMap(page => page.entries || []) ?? [];
  }, [query.data?.pages]);

  // Eagerly fetch all pages so we can compute previous values from the full history
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

  const allLoaded = !hasNextPage && !isFetchingNextPage && !query.isLoading;

  // Extract unique user IDs from entries for batch fetching
  const uniqueUserIds = useMemo(() => {
    const ids = entries
      .filter(entry => entry.actor?.actor_type === AuditActorType.AUDIT_ACTOR_TYPE_USER)
      .map(entry => entry.actor?.user?.user_id)
      .filter((id): id is string => !!id);
    return [...new Set(ids)];
  }, [entries]);

  // Fetch user details for all actors in history
  const usersQuery = useQuery({
    queryKey: ['users', 'byIds', uniqueUserIds],
    queryFn: () => userService.lookupUsers({ user_ids: uniqueUserIds }),
    enabled: uniqueUserIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const usersMap = useMemo(() => {
    const map = new Map<string, User>();
    if (usersQuery.data?.users) {
      for (const user of usersQuery.data.users) {
        if (user.id) {
          map.set(user.id, { id: user.id, email: user.email, name: user.name });
        }
      }
    }
    return map;
  }, [usersQuery.data?.users]);

  return {
    ...query,
    entries,
    allLoaded,
    usersMap,
    isLoadingUsers: usersQuery.isLoading && uniqueUserIds.length > 0,
  };
}
